import { jsx, jsxs } from "react/jsx-runtime";
import { CoverImage, BrandLogo } from "@seek/branding-assets";
import { Box, Notice, Text, Stack, Inline, Hidden, Columns, Column, Bleed } from "braid-design-system";
import { useMemo, forwardRef, useRef, useState, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { useJobDetailsViewContext } from "../CompanyProfile/CompanyProfile.mjs";
import { useMelwaysLink } from "@seek/melways-react";
import { StickyApply } from "../StickyApply/StickyApply.mjs";
import { squareBottomCornersOnDesktop } from "./JDVLayout.css.mjs";
const useShouldReduceDuplicateApply = () => {
  const { job: { isLinkOut, appliedDateFormatted } = {}, featureFlags } = useJobDetailsViewContext();
  return Boolean(
    (featureFlags == null ? void 0 : featureFlags.enableReducedDuplicatedApply) && !isLinkOut && appliedDateFormatted
  );
};
const jobUrlOriginValues = {
  stickyTitle: "stickyTitle",
  jobTitle: "jobTitle",
  showNewTab: "showNewTab"
};
const filterJobUrlValues = (values) => {
  const urlValues = {};
  Object.entries(values || {}).forEach(([key, value]) => {
    if (value) {
      urlValues[key] = value;
    }
  });
  return urlValues;
};
const constructFullUrl = ({
  hash = "",
  path,
  query
}) => {
  const queryString = Object.entries(query).map(([key, value]) => `${key}=${value}`).join("&");
  return `${path}${queryString ? `?${queryString}` : ""}${hash ? `#${hash}` : ""}`;
};
const useJobUrl = ({
  id,
  jobUrlOpts,
  renderRelativePaths,
  overrideValues
}) => {
  const urlResolver = useMelwaysLink();
  return useMemo(() => {
    var _a;
    if (!id) {
      return "about:blank";
    }
    const values = Object.entries(
      (jobUrlOpts == null ? void 0 : jobUrlOpts.params) || {}
    ).reduce((acc, [k, value]) => {
      if (value) {
        const key = k;
        if (overrideValues == null ? void 0 : overrideValues[key]) {
          acc[key] = overrideValues[key];
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});
    return urlResolver({
      path: constructFullUrl({
        hash: ((_a = jobUrlOpts == null ? void 0 : jobUrlOpts.meta) == null ? void 0 : _a.solMetadataReference) ? `sol=${jobUrlOpts == null ? void 0 : jobUrlOpts.meta.solMetadataReference}` : "",
        path: `/job/${id}`,
        query: filterJobUrlValues(values)
      }),
      product: "candidate",
      absolute: !renderRelativePaths
    });
  }, [id, urlResolver, jobUrlOpts, renderRelativePaths, overrideValues]);
};
const HKPrivateAdvertiserNotice = ({
  phoneNumber
}) => /* @__PURE__ */ jsx(Box, { paddingTop: "large", paddingBottom: ["large", "xlarge"], children: /* @__PURE__ */ jsx(Notice, { tone: "info", children: phoneNumber ? /* @__PURE__ */ jsxs(Text, { children: [
  "Interested in this role? Call ",
  phoneNumber,
  " to find out more."
] }) : /* @__PURE__ */ jsx(Text, { children: "This role is advertised privately with no contact details. To protect your privacy, candidates in Hong Kong can't apply." }) }) });
const MainContentCTAsWrapperView = ({
  actions,
  restrictedApplicationNotice,
  hideApplyButtonForPrivateAds,
  phoneNumber
}) => {
  const content = /* @__PURE__ */ jsx(Inline, { space: "small", children: actions });
  const privateAdvertiserNotice = hideApplyButtonForPrivateAds ? /* @__PURE__ */ jsx(HKPrivateAdvertiserNotice, { phoneNumber }) : null;
  return restrictedApplicationNotice ? /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    content,
    privateAdvertiserNotice,
    restrictedApplicationNotice
  ] }) : /* @__PURE__ */ jsxs(Stack, { space: "none", children: [
    content,
    privateAdvertiserNotice
  ] });
};
const MainContentCTAsWrapper = ({
  layoutOverride,
  actions,
  userHasAlreadyApplied,
  restrictedApplicationNotice,
  hideApplyButtonForPrivateAds,
  phoneNumber
}) => {
  const content = /* @__PURE__ */ jsx(
    MainContentCTAsWrapperView,
    {
      actions,
      restrictedApplicationNotice,
      hideApplyButtonForPrivateAds,
      phoneNumber
    }
  );
  if (userHasAlreadyApplied) {
    return content;
  }
  switch (layoutOverride) {
    case "desktop":
      return content;
    case "mobile":
      return null;
    default:
      return /* @__PURE__ */ jsx(Hidden, { below: "tablet", children: content });
  }
};
const MainContentCTAsWrapper$1 = forwardRef(
  (props, ref) => /* @__PURE__ */ jsx(Box, { ref, children: /* @__PURE__ */ jsx(MainContentCTAsWrapper, { ...props }) })
);
const boxPaddingX = (layoutOverride) => {
  switch (layoutOverride) {
    case "desktop":
      return "xlarge";
    case "mobile":
      return "xsmall";
    default:
      return {
        mobile: "xsmall",
        tablet: "gutter",
        desktop: "xlarge"
      };
  }
};
const MAIN_CONTENT_PADDING_Y = "xxlarge";
const JDVLayout = (props) => {
  var _a, _b, _c, _d;
  const {
    id,
    jobUrlOpts,
    renderRelativePaths,
    jobUrl,
    jobTitle,
    companyName,
    applyAction,
    saveAction,
    hideApplyButtonForPrivateAds,
    branding,
    editProps,
    badge,
    header,
    headerAdditional,
    mainContent,
    menu,
    showCoverThumbnail,
    layoutOverride,
    embedded,
    restrictedApplicationNotice,
    stickApplyButtonOnDesktop = false,
    onAnalyticsEvent,
    hideOpenInNewTab,
    hideJobLink,
    phoneNumber
  } = props;
  const showCover = (editProps == null ? void 0 : editProps.isEditMode) || Boolean((_a = branding == null ? void 0 : branding.assets) == null ? void 0 : _a.cover);
  const showLogo = (editProps == null ? void 0 : editProps.isEditMode) || Boolean((_b = branding == null ? void 0 : branding.assets) == null ? void 0 : _b.logo);
  const actionsRef = useRef();
  const [isBelowActions, setIsBelowActions] = useState(false);
  const { ref: actionsInViewRef } = useInView({
    onChange(inView) {
      var _a2;
      const actionsRect = ((_a2 = actionsRef == null ? void 0 : actionsRef.current) == null ? void 0 : _a2.getBoundingClientRect()) ?? {
        top: 0,
        bottom: 0
      };
      const isScrollingBelowActions = actionsRect.top >= 0 && actionsRect.bottom <= ((window == null ? void 0 : window.innerHeight) || (document == null ? void 0 : document.documentElement.clientHeight)) || actionsRect.bottom < 0 || actionsRect.top < 0;
      if (inView || !isScrollingBelowActions) {
        setIsBelowActions(false);
      } else {
        setIsBelowActions(true);
      }
    }
  });
  const setActionsRef = useCallback(
    (node) => {
      actionsRef.current = node;
      actionsInViewRef(node);
    },
    [actionsInViewRef]
  );
  const userHasAlreadyApplied = useShouldReduceDuplicateApply();
  const actions = /* @__PURE__ */ jsxs(Columns, { space: { mobile: "xxsmall", tablet: "small" }, children: [
    applyAction && !hideApplyButtonForPrivateAds ? /* @__PURE__ */ jsx(Column, { children: applyAction }) : null,
    saveAction ? /* @__PURE__ */ jsx(Column, { width: "content", children: saveAction }) : null
  ] });
  const paddingX = !embedded ? boxPaddingX(layoutOverride) : void 0;
  const showStickyApplyToolbar = stickApplyButtonOnDesktop && isBelowActions;
  const stickyJobUrl = useJobUrl({
    id,
    jobUrlOpts,
    overrideValues: {
      origin: jobUrlOriginValues.stickyTitle
    },
    renderRelativePaths
  });
  return /* @__PURE__ */ jsxs(
    Box,
    {
      background: !embedded ? "surface" : void 0,
      paddingTop: !embedded && !showCover ? "large" : void 0,
      paddingBottom: !embedded ? "large" : void 0,
      children: [
        /* @__PURE__ */ jsx(
          StickyApply,
          {
            jobTitle,
            jobUrl: hideJobLink || (jobUrlOpts == null ? void 0 : jobUrlOpts.hide) ? void 0 : jobUrl || stickyJobUrl,
            companyName,
            layoutOverride,
            actions,
            restrictedApplicationNotice,
            stickApplyButtonOnDesktop: showStickyApplyToolbar,
            applyAction,
            saveAction,
            onAnalyticsEvent,
            paddingX,
            embedded,
            hideOpenInNewTab,
            hideApplyButtonForPrivateAds
          }
        ),
        /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
          showCover ? /* @__PURE__ */ jsx(
            Box,
            {
              overflow: "hidden",
              borderRadius: { desktop: embedded ? "large" : "xlarge" },
              className: squareBottomCornersOnDesktop,
              children: /* @__PURE__ */ jsx(
                CoverImage,
                {
                  cover: (_c = branding == null ? void 0 : branding.assets) == null ? void 0 : _c.cover,
                  isEditMode: editProps == null ? void 0 : editProps.isEditMode,
                  onAddCoverClick: editProps == null ? void 0 : editProps.onAddCoverClick,
                  onRemoveCoverClick: editProps == null ? void 0 : editProps.onRemoveCoverClick,
                  onReplaceCoverClick: editProps == null ? void 0 : editProps.onReplaceCoverClick,
                  onRepositionCoverClick: editProps == null ? void 0 : editProps.onRepositionCoverClick,
                  showThumbnail: showCoverThumbnail,
                  showLoading: editProps == null ? void 0 : editProps.showCoverLoading
                }
              )
            }
          ) : null,
          /* @__PURE__ */ jsx(Box, { paddingX, children: /* @__PURE__ */ jsxs(Stack, { space: "xlarge", children: [
            /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
              /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                !showLogo ? badge : null,
                /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
                  /* @__PURE__ */ jsx(Column, { children: showLogo ? /* @__PURE__ */ jsx(
                    BrandLogo,
                    {
                      logo: (_d = branding == null ? void 0 : branding.assets) == null ? void 0 : _d.logo,
                      isEditMode: editProps == null ? void 0 : editProps.isEditMode,
                      withPlaceholder: editProps == null ? void 0 : editProps.isEditMode,
                      onAddLogoClick: editProps == null ? void 0 : editProps.onAddLogoClick,
                      onReplaceLogoClick: editProps == null ? void 0 : editProps.onReplaceLogoClick,
                      showLoading: editProps == null ? void 0 : editProps.showLogoLoading
                    }
                  ) : header }),
                  /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Inline, { space: "none", children: /* @__PURE__ */ jsx(Bleed, { top: !showLogo ? "xxsmall" : void 0, children: menu }) }) })
                ] })
              ] }),
              showLogo ? /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                badge,
                header
              ] }) : null,
              headerAdditional,
              /* @__PURE__ */ jsx(Hidden, { below: "tablet", children: Boolean(actions) && /* @__PURE__ */ jsx(
                MainContentCTAsWrapper$1,
                {
                  ref: setActionsRef,
                  layoutOverride,
                  actions,
                  userHasAlreadyApplied,
                  restrictedApplicationNotice,
                  hideApplyButtonForPrivateAds,
                  phoneNumber
                }
              ) })
            ] }),
            /* @__PURE__ */ jsx(Stack, { space: MAIN_CONTENT_PADDING_Y, children: mainContent })
          ] }) })
        ] })
      ]
    }
  );
};
export {
  JDVLayout,
  jobUrlOriginValues,
  useJobUrl,
  useShouldReduceDuplicateApply
};
